import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Target, Users, CalendarCheck, Library, Handshake, BriefcaseBusiness, SlidersHorizontal, Activity } from "lucide-react";

const metricConfig = [
  ["completed_community_goals", "Goals completed", Target],
  ["mutual_connections", "Mutual connections", Handshake],
  ["event_rsvps", "Event RSVPs", CalendarCheck],
  ["resources_shared", "Resources shared", Library],
  ["communities_joined", "Communities joined", Users],
  ["active_offerings", "Active offerings", BriefcaseBusiness],
  ["recommendation_feedback_actions", "Discovery controls used", SlidersHorizontal],
];

export default function OutcomesPage() {
  const [data, setData] = useState(null);
  const [momentum, setMomentum] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([api.get("/outcomes/me"), api.get("/outcomes/momentum")])
      .then(([outcomesResponse, momentumResponse]) => {
        if (!alive) return;
        setData(outcomesResponse.data);
        setMomentum(momentumResponse.data);
      })
      .catch((err) => alive && setError(err.response?.data?.detail || "Could not load your outcomes."));
    return () => { alive = false; };
  }, []);

  if (error) return <div className="brutal-card bg-card p-6">{error}</div>;
  if (!data || !momentum) return <div className="brutal-card bg-card p-6">Loading your HAEVEN impact…</div>;

  const metrics = data.metrics || {};

  return (
    <div data-testid="outcomes-page" className="space-y-6">
      <section className="brutal-card bg-sage p-6">
        <div className="text-xs uppercase tracking-[0.18em] font-bold">Meaningful outcomes</div>
        <div className="flex items-end gap-4 mt-2 flex-wrap">
          <div>
            <div className="font-display text-6xl leading-none">{data.score || 0}</div>
            <div className="text-sm opacity-75 mt-1">HAEVEN impact score</div>
          </div>
          <p className="max-w-xl text-sm pb-1">
            This score rewards participation, useful contribution, completed community goals, and real connections—not time spent scrolling.
          </p>
        </div>
      </section>

      <section className="brutal-card bg-card p-5">
        <div className="flex items-center gap-2">
          <Activity size={21} strokeWidth={2.4} />
          <div className="text-xs uppercase tracking-[0.18em] font-bold">Your last 7 days</div>
        </div>
        <div className="flex items-end gap-4 mt-3 flex-wrap">
          <div>
            <div className="font-display text-4xl capitalize">{momentum.level}</div>
            <div className="text-sm opacity-75">{momentum.useful_actions} capped useful actions</div>
          </div>
          <p className="max-w-xl text-sm">{momentum.next_action}</p>
        </div>
        <div className="mt-4 text-xs opacity-70 border-t-2 border-foreground pt-3">
          No streaks. No penalty for taking a day off. This pulse is private and does not measure screen time.
        </div>
      </section>

      <section>
        <h1 className="text-3xl mb-3">What you’re actually accomplishing</h1>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {metricConfig.map(([key, label, Icon]) => (
            <div key={key} className="brutal-card bg-card p-4">
              <Icon size={20} strokeWidth={2.4} />
              <div className="text-3xl font-display mt-3">{metrics[key] ?? 0}</div>
              <div className="text-sm mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="brutal-card bg-card p-5">
        <h2 className="text-2xl">Best next moves</h2>
        <p className="text-sm opacity-75 mt-1">Small actions chosen to improve connection and usefulness rather than maximize attention.</p>
        <div className="mt-4 space-y-2">
          {(data.guidance || []).length === 0 ? (
            <div className="border-2 border-foreground rounded-xl p-3">You have a strong participation footprint. Keep helping people and communities finish meaningful work.</div>
          ) : data.guidance.map((item, index) => (
            <div key={`${index}-${item}`} className="border-2 border-foreground rounded-xl p-3 flex gap-3">
              <span className="font-display">{index + 1}</span>
              <span className="text-sm">{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="brutal-card bg-accent p-4 text-sm">
        <strong>HAEVEN principle:</strong> meaningful outcomes over attention. These numbers are intended as personal feedback—not a public popularity leaderboard.
      </section>
    </div>
  );
}
